/**
 * AuthLogger - 통합 인증 플로우 로깅 유틸리티
 *
 * 역할:
 * - 로그인/회원가입 플로우의 모든 단계를 통일된 형식으로 로깅
 * - 동일한 prefix와 세션 ID로 플로우 추적 가능
 * - 개인정보 마스킹 및 API 응답 최소화
 * - Datadog 통합으로 프로덕션 모니터링
 */
export class AuthLogger {
  private static readonly PREFIX = '[AUTH-FLOW]'
  private static sessionId = Date.now()

  // Datadog 로그 레벨 매핑
  private static readonly LOG_LEVELS = {
    INFO: 'info',
    ERROR: 'error',
    WARNING: 'warn',
    DEBUG: 'debug',
  } as const

  /**
   * 새로운 플로우 시작 시 세션 ID 재설정
   */
  static resetSession(): void {
    this.sessionId = Date.now()
  }

  /**
   * Datadog에 로그 전송 (서버 사이드 전용)
   */
  private static async sendToDatadog(
    level: keyof typeof AuthLogger.LOG_LEVELS,
    message: string,
    data?: any,
  ): Promise<void> {
    if (typeof window === 'undefined') {
      // 서버 사이드만
      try {
        const { tracer } = await import('dd-trace')
        const activeSpan = tracer.scope().active()

        if (activeSpan) {
          activeSpan.log({
            level: this.LOG_LEVELS[level],
            message: `${this.PREFIX}[${this.sessionId}] ${message}`,
            sessionId: this.sessionId,
            ...data,
            timestamp: new Date().toISOString(),
          })
        }
      } catch (e) {
        // Datadog 전송 실패는 무시 (로깅이 앱 동작을 막으면 안됨)
        if (process.env.NODE_ENV !== 'production') {
          console.error('Datadog 로그 전송 실패:', e)
        }
        // fallback으로 기본 서버 로그 사용
        console.log(`${this.PREFIX}[${this.sessionId}] ${message}`, data)
      }
    }
  }

  /**
   * 일반 단계 로깅 - 성공한 작업이나 진행 상황 기록
   * @param step 단계 설명
   * @param data 관련 데이터 (개인정보는 미리 마스킹 필요)
   */
  static step(step: string, data?: any): void {
    const logData = {
      ...data,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
    }

    // 콘솔 로그 (개발 환경에서만)
    if (process.env.NODE_ENV !== 'production') {
      console.log(`${this.PREFIX}[${this.sessionId}] ✅ ${step}`, logData)
    }

    // Datadog 전송 (비동기, 논블로킹)
    this.sendToDatadog('INFO', step, logData).catch(() => {
      // 에러 무시
    })
  }

  /**
   * 에러 로깅 - API 응답 데이터 최소화하여 핵심 정보만 기록
   * @param step 실패한 단계 설명
   * @param error 에러 객체 (axios error, API error 등)
   */
  static error(step: string, error: any): void {
    const errorInfo = {
      message: error?.message || 'Unknown error',
      status: error?.response?.status || error?.status,
      apiMessage: error?.response?.data?.message || error?.response?.data?.error,
      type: error?.response?.data?.reason || error?.code,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
    }

    // 프로덕션에서도 에러는 콘솔에 기록
    console.error(`${this.PREFIX}[${this.sessionId}] ❌ ${step}`, errorInfo)

    // Datadog 에러 캡처 (비동기)
    if (typeof window === 'undefined') {
      import('@/utils/datadog/error-capture')
        .then(({ captureError }) => {
          captureError(error, {
            endpoint: step,
            sessionId: this.sessionId.toString(),
            ...errorInfo,
          })
        })
        .catch(() => {
          // 에러 무시
        })
    }

    // Datadog 로그 전송 (비동기)
    this.sendToDatadog('ERROR', step, errorInfo).catch(() => {
      // 에러 무시
    })
  }

  /**
   * reCAPTCHA 검증 결과 로깅
   * @param action reCAPTCHA 액션 ('email_login', 'sns_login', etc.)
   * @param result 검증 결과 객체
   */
  static recaptcha(action: string, result: any): void {
    const recaptchaInfo = {
      action,
      score: result?.score ?? 'N/A',
      success: result?.success ?? result?.verified ?? false,
      errorCode: result?.errorCode,
      reason:
        result?.['error-codes'] || result?.reason || (result?.success === false ? 'VERIFICATION_FAILED' : undefined),
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
    }

    // 실패 시 WARNING 레벨로 기록
    const level = recaptchaInfo.success ? 'INFO' : 'WARNING'

    // 콘솔 로그 (개발 환경에서만)
    if (process.env.NODE_ENV !== 'production') {
      console.log(`${this.PREFIX}[${this.sessionId}] 🛡️ reCAPTCHA`, recaptchaInfo)
    }

    // Datadog 전송 (비동기)
    this.sendToDatadog(level as keyof typeof AuthLogger.LOG_LEVELS, `reCAPTCHA ${action}`, recaptchaInfo).catch(() => {
      // 에러 무시
    })
  }

  /**
   * 이메일 마스킹 유틸리티 (개인정보 보호)
   * @param email 이메일 주소
   * @returns 마스킹된 이메일 (예: 'tes***@exam***')
   */
  static maskEmail(email: string): string {
    if (!email || typeof email !== 'string') {
      return 'unknown'
    }

    const [localPart, domain] = email.split('@')
    if (!localPart || !domain) {
      return 'invalid'
    }

    const maskedLocal = localPart.length > 3 ? localPart.substring(0, 3) + '***' : '***'

    const maskedDomain = domain.length > 4 ? domain.substring(0, 3) + '***' : '***'

    return `${maskedLocal}@${maskedDomain}`
  }

  /**
   * User Agent 문자열 단축 (로그 가독성 향상)
   * @param userAgent User Agent 문자열
   * @returns 단축된 User Agent (최대 80자)
   */
  static truncateUserAgent(userAgent?: string): string {
    if (!userAgent || typeof userAgent !== 'string') {
      return 'unknown'
    }

    return userAgent.length > 80 ? userAgent.substring(0, 80) + '...' : userAgent
  }

  /**
   * IP 주소에서 첫 번째 IP만 추출 (프록시 체인에서)
   * @param ipHeader X-Forwarded-For 헤더 값
   * @returns 첫 번째 IP 주소
   */
  static extractFirstIP(ipHeader?: string): string {
    if (!ipHeader || typeof ipHeader !== 'string') {
      return 'unknown'
    }

    return ipHeader.split(',')[0].trim()
  }

  /**
   * 개발 환경에서만 상세 로그를 위한 헬퍼
   * @param message 로그 메시지
   * @param data 로그 데이터
   */
  static debug(message: string, data?: any): void {
    if (process.env.NODE_ENV === 'development') {
      console.log(`${this.PREFIX}[${this.sessionId}] 🔍 DEBUG: ${message}`, data)
    }
  }
}

export default AuthLogger
